/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react'
import PropTypes from 'prop-types'
import {Stack, Fade, Text, Box} from '@salesforce/retail-react-app/app/components/shared/ui'
import useRegistrationFields from '../../components/forms/useRegistrationFields'
import Field from '../field'
import PasswordRequirements from '../../components/forms/password-requirements'
import {FormattedMessage, useIntl} from 'react-intl'
import {useBrand} from '../../contexts/brand-context'
import {usePhoneValidation, useEmailValidation} from '../../integrations/melissa/hooks/use-melissa'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {SITE_COUNTRY_MAP} from '../../constants'

const RegistrationFields = ({form, prefix = ''}) => {
    const {isGarage} = useBrand()
    const fields = useRegistrationFields({form, prefix})
    const password = form.watch(`${prefix}password`)
    const intl = useIntl()
    const {site} = useMultiSite()
    const defaultCountry = SITE_COUNTRY_MAP[site?.id] || 'CA'

    const {fetchEmailValidation} = useEmailValidation()
    const {fetchPhoneValidation} = usePhoneValidation()

    const handleEmailBlur = async (e) => {
        const value = e.target.value

        if (!value) return

        const result = await fetchEmailValidation({
            email: value,
            shouldFetch: true
        })

        const isValid = result?.emailValidationResult?.isValidEmail

        if (!isValid) {
            form.setError(fields.email.name, {
                type: 'manual',
                message: intl.formatMessage({
                    defaultMessage: 'Invalid email address',
                    id: 'address.email.invalid'
                })
            })
        } else {
            form.clearErrors(fields.email.name)
        }
    }

    const handlePhoneBlur = async (e) => {
        const value = e.target.value
        const digits = (value || '').replace(/[^\d]/g, '')

        if (digits.length !== 10) return

        const result = await fetchPhoneValidation({
            phoneNumber: value,
            countryCode: defaultCountry,
            shouldFetch: true
        })

        const isValid = result?.phoneValidationResult?.isValidPhone

        if (!isValid) {
            form.setError(fields.phone.name, {
                type: 'manual',
                message: intl.formatMessage({
                    defaultMessage: 'Invalid phone number',
                    id: 'address.phone.invalid'
                })
            })
        } else {
            form.clearErrors(fields.phone.name)
        }
    }

    return (
        <Stack spacing={4}>
            <Stack spacing={2}>
                <Field
                    {...fields.email}
                    inputProps={({onChange, value}) => ({
                        ...fields.email.inputProps?.({onChange, value}),
                        onBlur: handleEmailBlur
                    })}
                />
                <Field {...fields.password} />
                <PasswordRequirements value={password} />
                <Field
                    {...fields.phone}
                    inputProps={({onChange, value}) => ({
                        ...fields.phone.inputProps({onChange, value}),
                        onBlur: handlePhoneBlur
                    })}
                />
                <Field {...fields.birthDay} />
                <Text
                    fontSize="12px"
                    color="webTypo.textSecondary"
                    fontWeight="normal"
                    mb="3"
                    lineHeight="1.5"
                >
                    {isGarage ? (
                        <FormattedMessage
                            id="login_birthday.loyalty.gift.garage"
                            defaultMessage="Add your birthday to get a special gift every year! You cannot change it afterwards."
                        />
                    ) : (
                        <FormattedMessage
                            id="login_birthday.loyalty.gift.dynamite"
                            defaultMessage="Add your birthday to get a special gift every year! You cannot change it afterwards."
                        />
                    )}
                </Text>
            </Stack>

            <Field {...fields.termsAndConditions} />
            <Field {...fields.emailSubscribe} />
        </Stack>
    )
}

RegistrationFields.propTypes = {
    /** Object returned from `useForm` */
    form: PropTypes.object.isRequired,

    /** Optional prefix for field names */
    prefix: PropTypes.string
}

export default RegistrationFields
